import { api } from './api'
import {
  CampaignDataProps,
  CampaignMetricsProps,
  type CampaignAudienceType,
} from '@/types/campaignProps'

interface WrappedPayload<T> {
  data?: T
  items?: T
}

function unwrapPayload<T>(payload: T | WrappedPayload<T>) {
  if (typeof payload === 'object' && payload !== null) {
    if ('data' in payload && payload.data !== undefined) {
      return payload.data as T
    }

    if ('items' in payload && payload.items !== undefined) {
      return payload.items as T
    }
  }

  return payload as T
}

export function normalizeCampaignsPayload(
  payload: unknown,
): CampaignDataProps[] {
  const unwrapped = unwrapPayload(
    payload as CampaignDataProps[] | WrappedPayload<CampaignDataProps[]>,
  )

  if (Array.isArray(unwrapped)) {
    return unwrapped
  }

  if (
    typeof unwrapped === 'object' &&
    unwrapped !== null &&
    'data' in unwrapped &&
    Array.isArray((unwrapped as { data?: CampaignDataProps[] }).data)
  ) {
    return (unwrapped as { data: CampaignDataProps[] }).data
  }

  return []
}

export async function fetchCampaigns() {
  const response = await api.get('/admin/campaigns')
  return normalizeCampaignsPayload(response.data)
}

export async function fetchCampaignMetrics(campaignId: string) {
  const response = await api.get<
    CampaignMetricsProps | WrappedPayload<CampaignMetricsProps>
  >(`/admin/campaigns/${campaignId}/metrics`)

  return unwrapPayload(response.data)
}

export interface CreateCampaignPayload {
  type: CampaignDataProps['type']
  title: string
  body: string
  imageUrl?: string
  ctaType: CampaignDataProps['ctaType']
  ctaValue?: string
  startsAt?: string
  expiresAt?: string
}

export async function createCampaign(payload: CreateCampaignPayload) {
  const response = await api.post<
    CampaignDataProps | WrappedPayload<CampaignDataProps>
  >('/admin/campaigns', payload)

  return unwrapPayload(response.data)
}

export async function updateCampaign(
  campaignId: string,
  payload: CreateCampaignPayload,
) {
  const response = await api.patch<
    CampaignDataProps | WrappedPayload<CampaignDataProps>
  >(`/admin/campaigns/${campaignId}`, payload)

  return unwrapPayload(response.data)
}

export async function publishCampaign(
  campaignId: string,
  audienceType: CampaignAudienceType,
  audienceValue?: string,
) {
  await api.post(`/admin/campaigns/${campaignId}/publish`, {
    audienceType,
    audienceValue,
  })
}
